import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  LoaderCircle,
  Mail,
  Phone,
  ReceiptText,
  ShieldCheck,
  Smartphone,
  Sun,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildInternationalPhoneNumber,
  DEFAULT_PHONE_COUNTRY_CODE,
  KAJIKAJY_PLANS,
  KAJIKAJY_PHONE_COUNTRIES,
  PAPI_PAYMENT_ORIGIN,
  WEB_I18N,
  WEB_LOCALES,
  WEB_SOCIAL_ICONS,
  WEB_SOCIAL_LINKS,
  WEB_SITE_PATHS,
  applyKajikajyTheme,
  clearPendingPayment,
  formatLocalPhoneForCountry,
  getInitialKajikajyTheme,
  getInitialWebLocale,
  getPhoneCountry,
  isAllowedKajikajyPaymentOrigin,
  isKajikajyPaymentReturnMessage,
  isPapiPaymentStatusMessage,
  mapInvoiceStatusToPaymentPhase,
  navigateToPaymentCheckout,
  onlyPhoneDigits,
  openCenteredPaymentPopup,
  parsePaymentReturnQuery,
  persistKajikajyTheme,
  persistPendingPayment,
  persistWebLocale,
  readPendingPayment,
  readSharedKajikajyTheme,
  readSharedWebLocale,
  relayPaymentReturnToOpener,
  removePaymentReturnQuery,
  resolveWebLocale,
  type KajikajyPlanId,
  type PaymentFlowPhase,
  type PendingPayment,
  type KajikajyThemeMode,
  type WebLocale,
} from "@kajikajy/shared";
import {
  createPaymentSession,
  createInvoice,
  ensureSubscriptionAuth,
  getPaymentStatus,
  type CreateInvoiceResult,
  type CreatePaymentSessionResult,
} from "./lib/paymentsApi";

const plans = Object.values(KAJIKAJY_PLANS);

type CheckoutPhase =
  | "editing"
  | "creating_invoice"
  | "creating_payment"
  | "payment_ready";

type VerificationMode = "polling" | "once";

function getBrowserLocale(): WebLocale {
  if (typeof navigator === "undefined") {
    return "fr";
  }
  return resolveWebLocale(navigator.language.slice(0, 2), "fr");
}

function useThemeMode() {
  const [theme, setTheme] = useState<KajikajyThemeMode>(
    getInitialKajikajyTheme,
  );

  useEffect(() => {
    applyKajikajyTheme(theme);
    persistKajikajyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const syncSharedTheme = () => {
      const sharedTheme = readSharedKajikajyTheme();
      if (sharedTheme) {
        setTheme((current) =>
          current === sharedTheme ? current : sharedTheme,
        );
      }
    };
    const syncVisibleTheme = () => {
      if (!document.hidden) {
        syncSharedTheme();
      }
    };

    window.addEventListener("focus", syncSharedTheme);
    window.addEventListener("pageshow", syncSharedTheme);
    document.addEventListener("visibilitychange", syncVisibleTheme);
    return () => {
      window.removeEventListener("focus", syncSharedTheme);
      window.removeEventListener("pageshow", syncSharedTheme);
      document.removeEventListener("visibilitychange", syncVisibleTheme);
    };
  }, []);

  return {
    theme,
    toggleTheme: () =>
      setTheme((current) => (current === "dark" ? "light" : "dark")),
  };
}

function isLocalHostname() {
  if (typeof window === "undefined") {
    return false;
  }

  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function getSiteHref(path = "/") {
  return isLocalHostname()
    ? `http://localhost:4321${path}`
    : `https://kajikajy.com${path}`;
}

function getAccountHomeHref() {
  return isLocalHostname()
    ? "http://localhost:5175/"
    : "https://compte.kajikajy.com/";
}

function isKajikajyPlanId(value: string | null): value is KajikajyPlanId {
  return value === "essor" || value === "excellence";
}

function getInitialPlanId(): KajikajyPlanId {
  if (typeof window === "undefined") {
    return "essor";
  }

  const requestedPlanId = new URLSearchParams(window.location.search).get(
    "plan",
  );
  return isKajikajyPlanId(requestedPlanId) ? requestedPlanId : "essor";
}

function getInitialLocale(): WebLocale {
  return getInitialWebLocale(getBrowserLocale());
}

function useWebLocale() {
  const [locale, setLocale] = useState<WebLocale>(getInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    persistWebLocale(locale);
  }, [locale]);

  useEffect(() => {
    const syncSharedLocale = () => {
      const sharedLocale = readSharedWebLocale();
      if (sharedLocale) {
        setLocale((current) =>
          current === sharedLocale ? current : sharedLocale,
        );
      }
    };
    const syncVisibleLocale = () => {
      if (!document.hidden) {
        syncSharedLocale();
      }
    };

    window.addEventListener("focus", syncSharedLocale);
    window.addEventListener("pageshow", syncSharedLocale);
    document.addEventListener("visibilitychange", syncVisibleLocale);
    return () => {
      window.removeEventListener("focus", syncSharedLocale);
      window.removeEventListener("pageshow", syncSharedLocale);
      document.removeEventListener("visibilitychange", syncVisibleLocale);
    };
  }, []);

  return { locale, setLocale };
}

function ThemeMoonIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3a6 6 0 0 0 9 6 9 9 0 1 1-9-6Z" />
    </svg>
  );
}

function FooterSocialLinks() {
  return (
    <div className="footer-social-links">
      <a
        aria-label="LinkedIn"
        className="footer-social-link"
        href={WEB_SOCIAL_LINKS.linkedin}
        rel="noopener noreferrer"
        target="_blank"
      >
        <svg
          aria-hidden="true"
          focusable="false"
          viewBox={WEB_SOCIAL_ICONS.linkedin.viewBox}
        >
          <path fill="currentColor" d={WEB_SOCIAL_ICONS.linkedin.path} />
        </svg>
      </a>
      <a
        aria-label="Facebook"
        className="footer-social-link"
        href={WEB_SOCIAL_LINKS.facebook}
        rel="noopener noreferrer"
        target="_blank"
      >
        <svg
          aria-hidden="true"
          focusable="false"
          viewBox={WEB_SOCIAL_ICONS.facebook.viewBox}
        >
          <path fill="currentColor" d={WEB_SOCIAL_ICONS.facebook.path} />
        </svg>
      </a>
    </div>
  );
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidLocalPhone(countryCode: string, value: string): boolean {
  const digits = onlyPhoneDigits(value);
  return countryCode === DEFAULT_PHONE_COUNTRY_CODE
    ? digits.length === 9
    : digits.length >= 6;
}

function formatMoney(amount: number): string {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return "-";
  }
  return `${new Intl.NumberFormat("fr-MG").format(numericAmount)} Ar`;
}

function resolveAmountMGA(...amounts: unknown[]): number {
  for (const amount of amounts) {
    const numericAmount = Number(amount);
    if (Number.isFinite(numericAmount) && numericAmount > 0) {
      return numericAmount;
    }
  }
  return 0;
}

type LanguageMenuProps = {
  label: string;
  locale: WebLocale;
  onLocaleChange: (locale: WebLocale) => void;
};

function LanguageMenu({ label, locale, onLocaleChange }: LanguageMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnOutside = (event: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="kj-language-menu" ref={menuRef}>
      <button
        className="kj-language-trigger"
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-locale-trigger
        onClick={() => setOpen((current) => !current)}
      >
        <span data-locale-current>{WEB_I18N[locale].languageCode}</span>
        <ChevronDown aria-hidden size={15} />
      </button>
      {open ? (
        <div className="kj-language-popover" role="listbox" aria-label={label}>
          {WEB_LOCALES.map((option) => (
            <button
              className="kj-language-option"
              key={option}
              type="button"
              role="option"
              aria-selected={option === locale}
              data-locale-option={option}
              onClick={() => {
                onLocaleChange(option);
                setOpen(false);
              }}
            >
              {WEB_I18N[option].languageName}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type PhoneCountryFieldProps = {
  label: string;
  countryLabel: string;
  searchPlaceholder: string;
  noResults: string;
  placeholder: string;
  countryCode: string;
  localNumber: string;
  onCountryCodeChange: (countryCode: string) => void;
  onLocalNumberChange: (localNumber: string) => void;
};

function PhoneCountryField({
  label,
  countryLabel,
  searchPlaceholder,
  noResults,
  placeholder,
  countryCode,
  localNumber,
  onCountryCodeChange,
  onLocalNumberChange,
}: PhoneCountryFieldProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const fieldRef = useRef<HTMLDivElement>(null);
  const selectedCountry = getPhoneCountry(countryCode);

  const filteredCountries = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return KAJIKAJY_PHONE_COUNTRIES;
    }

    return KAJIKAJY_PHONE_COUNTRIES.filter(
      (country) =>
        country.name.toLowerCase().includes(normalizedQuery) ||
        country.code.includes(normalizedQuery),
    );
  }, [query]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnOutside = (event: PointerEvent) => {
      if (
        fieldRef.current &&
        !fieldRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="kj-field phone-field" ref={fieldRef}>
      <span className="field-label">
        <Phone aria-hidden size={15} /> {label}
      </span>
      <div className="phone-control">
        <button
          className="phone-country-trigger"
          type="button"
          aria-label={countryLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <span className="phone-country-flag" aria-hidden>
            {selectedCountry.flag}
          </span>
          <span>{selectedCountry.code}</span>
          <ChevronDown aria-hidden size={15} />
        </button>
        <input
          className="phone-number-input"
          autoComplete="tel-national"
          inputMode="tel"
          placeholder={placeholder}
          value={localNumber}
          onChange={(event) => {
            onLocalNumberChange(
              formatLocalPhoneForCountry(event.target.value, countryCode),
            );
          }}
        />
        {open ? (
          <div
            className="phone-country-popover"
            role="listbox"
            aria-label={countryLabel}
          >
            <input
              className="phone-country-search"
              aria-label={searchPlaceholder}
              autoComplete="off"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className="phone-country-list">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <button
                    className="phone-country-option"
                    key={country.code}
                    type="button"
                    role="option"
                    aria-selected={country.code === countryCode}
                    onClick={() => {
                      onCountryCodeChange(country.code);
                      onLocalNumberChange("");
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    <span className="phone-country-flag" aria-hidden>
                      {country.flag}
                    </span>
                    <span className="phone-country-name">{country.name}</span>
                    <span className="phone-country-code">{country.code}</span>
                  </button>
                ))
              ) : (
                <span className="phone-country-empty">{noResults}</span>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

type PaymentDialogProps = {
  accountHref: string;
  checkoutUrl: string | null;
  error: string;
  flowPhase: PaymentFlowPhase;
  onClose: () => void;
  onContinueSameTab: () => void;
  onPay: () => void;
  onRetry: () => void;
  onVerify: () => void;
  payment: CreatePaymentSessionResult | null;
  phase: CheckoutPhase;
  sameTabAvailable: boolean;
  text: (typeof WEB_I18N)[WebLocale]["subscribe"];
};

function PaymentDialog({
  accountHref,
  checkoutUrl,
  error,
  flowPhase,
  onClose,
  onContinueSameTab,
  onPay,
  onRetry,
  onVerify,
  payment,
  phase,
  sameTabAvailable,
  text,
}: PaymentDialogProps) {
  const isLoading =
    phase === "creating_invoice" || phase === "creating_payment";
  const loadingLabel =
    phase === "creating_invoice" ? text.creatingInvoice : text.creatingPayment;
  const statusCopy =
    flowPhase === "popup_open"
      ? {
          title: text.paymentOpeningTitle,
          body: text.paymentOpeningText,
          tone: "waiting",
        }
      : flowPhase === "verifying"
        ? {
            title: text.paymentVerifyingTitle,
            body: text.paymentVerifyingText,
            tone: "waiting",
          }
        : flowPhase === "interrupted"
          ? {
              title: text.paymentInterruptedTitle,
              body: text.paymentInterruptedText,
              tone: "interrupted",
            }
          : flowPhase === "delayed"
            ? {
                title: text.paymentDelayedTitle,
                body: text.paymentDelayedText,
                tone: "waiting",
              }
            : flowPhase === "succeeded"
              ? {
                  title: text.paymentSuccessTitle,
                  body: text.paymentSuccessText,
                  tone: "success",
                }
              : flowPhase === "failed"
                ? {
                    title: text.paymentFailureTitle,
                    body: text.paymentFailureText,
                    tone: "failure",
                  }
                : flowPhase === "popup_blocked"
                  ? {
                      title: text.paymentPopupBlockedTitle,
                      body: text.paymentPopupBlockedText,
                      tone: "failure",
                    }
                  : null;

  const dialogRef = useRef<HTMLElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(
    typeof document === "undefined"
      ? null
      : (document.activeElement as HTMLElement | null),
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocusedRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    if (flowPhase === "popup_open") {
      return;
    }
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    const activeElement = document.activeElement as HTMLElement | null;
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    if (
      activeElement &&
      dialog.contains(activeElement) &&
      activeElement.matches(focusableSelector)
    ) {
      return;
    }
    const firstControl = dialog.querySelector<HTMLElement>(focusableSelector);
    (firstControl ?? dialog).focus();
  }, [checkoutUrl, error, flowPhase, isLoading, payment, sameTabAvailable]);

  useEffect(() => {
    if (flowPhase !== "popup_open") {
      return undefined;
    }
    const restoreDialogFocus = () => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      const firstControl = dialog.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (firstControl ?? dialog).focus();
    };
    window.addEventListener("focus", restoreDialogFocus);
    return () => window.removeEventListener("focus", restoreDialogFocus);
  }, [flowPhase]);

  return (
    <div className="payment-modal">
      <div className="payment-modal-backdrop" aria-hidden="true" />
      <section
        ref={dialogRef}
        className="payment-dialog"
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="payment-dialog-title"
        aria-describedby="payment-dialog-description"
        aria-busy={
          isLoading || flowPhase === "popup_open" || flowPhase === "verifying"
        }
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClose();
            return;
          }
          if (event.key !== "Tab") {
            return;
          }
          const focusable = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>(
              'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((element) => element.getClientRects().length > 0);
          if (focusable.length === 0) {
            event.preventDefault();
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <button
          autoFocus
          className="payment-dialog-close"
          type="button"
          aria-label={text.closeButton}
          onClick={onClose}
        >
          <X aria-hidden size={18} />
        </button>

        <div className="payment-dialog-heading">
          <CreditCard aria-hidden size={22} />
          <div>
            <p className="panel-kicker">{text.paymentDialogTitle}</p>
            <h2 id="payment-dialog-title">
              {statusCopy?.title ??
                (payment ? text.invoiceReadyTitle : loadingLabel)}
            </h2>
          </div>
        </div>

        {isLoading ? (
          <div
            className="payment-dialog-loader"
            role="status"
            aria-live="polite"
          >
            <LoaderCircle aria-hidden size={40} />
            <p id="payment-dialog-description">{text.paymentPreparingText}</p>
          </div>
        ) : statusCopy ? (
          <>
            <div
              className={`payment-dialog-status payment-dialog-status--${statusCopy.tone}`}
              role={flowPhase === "failed" ? "alert" : "status"}
              aria-live={flowPhase === "failed" ? "assertive" : "polite"}
            >
              {statusCopy.tone === "waiting" ? (
                <LoaderCircle
                  className="payment-dialog-status-icon payment-dialog-status-icon--spin"
                  aria-hidden
                  size={38}
                />
              ) : statusCopy.tone === "success" ? (
                <CheckCircle2
                  className="payment-dialog-status-icon"
                  aria-hidden
                  size={38}
                />
              ) : statusCopy.tone === "interrupted" ? (
                <X
                  className="payment-dialog-status-icon"
                  aria-hidden
                  size={38}
                />
              ) : statusCopy.tone === "failure" ? (
                <X
                  className="payment-dialog-status-icon"
                  aria-hidden
                  size={38}
                />
              ) : null}
              <p id="payment-dialog-description">{statusCopy.body}</p>
            </div>
            <div className="payment-dialog-actions">
              {flowPhase === "popup_blocked" ? (
                <>
                  <button
                    className="kj-button kj-button--primary kj-button--block"
                    type="button"
                    onClick={onPay}
                  >
                    {text.reopenPaymentButton}
                  </button>
                  <button
                    className="kj-button kj-button--secondary kj-button--block"
                    type="button"
                    disabled={!sameTabAvailable}
                    onClick={onContinueSameTab}
                  >
                    {text.continueSameTabButton}
                  </button>
                  {!sameTabAvailable ? (
                    <p className="payment-dialog-error" role="alert">
                      {text.paymentStorageUnavailableText}
                    </p>
                  ) : null}
                </>
              ) : null}
              {flowPhase === "interrupted" ? (
                <>
                  <button
                    className="kj-button kj-button--primary kj-button--block"
                    type="button"
                    onClick={onVerify}
                  >
                    {text.checkAgainButton}
                  </button>
                  <button
                    className="kj-button kj-button--secondary kj-button--block"
                    type="button"
                    onClick={onPay}
                  >
                    {text.reopenPaymentButton}
                  </button>
                </>
              ) : null}
              {flowPhase === "delayed" ? (
                <>
                  <button
                    className="kj-button kj-button--primary kj-button--block"
                    type="button"
                    onClick={onVerify}
                  >
                    {text.checkAgainButton}
                  </button>
                  {checkoutUrl ? (
                    <button
                      className="kj-button kj-button--secondary kj-button--block"
                      type="button"
                      onClick={onPay}
                    >
                      {text.reopenPaymentButton}
                    </button>
                  ) : null}
                </>
              ) : null}
              {flowPhase === "succeeded" ? (
                <a
                  className="kj-button kj-button--primary kj-button--block"
                  href={accountHref}
                >
                  {text.goToAccountButton}
                </a>
              ) : null}
              {flowPhase === "failed" ? (
                <button
                  className="kj-button kj-button--primary kj-button--block"
                  type="button"
                  onClick={onRetry}
                >
                  {text.retryButton}
                </button>
              ) : null}
              <button
                className="kj-button kj-button--secondary kj-button--block"
                type="button"
                onClick={onClose}
              >
                {text.closeButton}
              </button>
            </div>
          </>
        ) : payment ? (
          <>
            <p id="payment-dialog-description">{text.paymentDialogText}</p>

            <dl className="payment-dialog-details">
              <div>
                <dt>{text.invoiceReferenceLabel}</dt>
                <dd>{payment.paymentReference}</dd>
              </div>
              <div>
                <dt>{text.amountLabel}</dt>
                <dd>{formatMoney(payment.amountMGA)}</dd>
              </div>
              <div>
                <dt>{text.paymentProviderLabel}</dt>
                <dd>{payment.providerLabel}</dd>
              </div>
            </dl>

            {!payment.checkoutUrl ? (
              <p className="payment-dialog-error">
                {text.paymentUnavailableText}
              </p>
            ) : null}

            <div className="payment-dialog-actions">
              <button
                className="kj-button kj-button--primary kj-button--block"
                type="button"
                disabled={!payment.checkoutUrl}
                onClick={onPay}
              >
                {text.continueButton}
              </button>
              <button
                className="kj-button kj-button--secondary kj-button--block"
                type="button"
                onClick={onClose}
              >
                {text.closeButton}
              </button>
            </div>
          </>
        ) : (
          <>
            <p
              className="payment-dialog-error"
              id="payment-dialog-description"
              role="alert"
            >
              {error || text.invoiceError}
            </p>
            <div className="payment-dialog-actions">
              <button
                className="kj-button kj-button--primary kj-button--block"
                type="button"
                onClick={onRetry}
              >
                {text.retryButton}
              </button>
              <button
                className="kj-button kj-button--secondary kj-button--block"
                type="button"
                onClick={onClose}
              >
                {text.closeButton}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export function App() {
  const [selectedPlanId, setSelectedPlanId] =
    useState<KajikajyPlanId>(getInitialPlanId);
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    DEFAULT_PHONE_COUNTRY_CODE,
  );
  const [localPhoneNumber, setLocalPhoneNumber] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirmation, setPinConfirmation] = useState("");
  const [checkoutPhase, setCheckoutPhase] = useState<CheckoutPhase>("editing");
  const [invoice, setInvoice] = useState<CreateInvoiceResult | null>(null);
  const [paymentSession, setPaymentSession] =
    useState<CreatePaymentSessionResult | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [paymentFlowPhase, setPaymentFlowPhase] =
    useState<PaymentFlowPhase>("ready");
  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(
    null,
  );
  const [paymentPersistenceAvailable, setPaymentPersistenceAvailable] =
    useState(true);
  const [verificationActive, setVerificationActive] = useState(false);
  const [verificationMode, setVerificationMode] =
    useState<VerificationMode>("polling");
  const [verificationNonce, setVerificationNonce] = useState(0);
  const checkoutPanelRef = useRef<HTMLElement>(null);
  const paymentPopupRef = useRef<Window | null>(null);
  const pendingPaymentRef = useRef<PendingPayment | null>(null);
  const paymentPreparationDismissedRef = useRef(false);
  const popupMonitorRef = useRef<number | null>(null);
  const verificationStartedAtRef = useRef(0);
  const checkoutRequestIdRef = useRef(0);
  const previousCheckoutFormRef = useRef(
    [
      selectedPlanId,
      businessName,
      email,
      phoneCountryCode,
      localPhoneNumber,
      pin,
      pinConfirmation,
    ].join("\u0000"),
  );
  const { theme, toggleTheme } = useThemeMode();
  const { locale, setLocale } = useWebLocale();
  const text = WEB_I18N[locale];

  function stopPopupMonitor() {
    if (popupMonitorRef.current !== null) {
      window.clearInterval(popupMonitorRef.current);
      popupMonitorRef.current = null;
    }
  }

  function closeTrackedPaymentPopup() {
    stopPopupMonitor();
    const popup = paymentPopupRef.current;
    paymentPopupRef.current = null;
    if (popup && !popup.closed) {
      popup.close();
    }
  }

  function updatePendingPayment(nextPayment: PendingPayment | null) {
    pendingPaymentRef.current = nextPayment;
    setPendingPayment(nextPayment);
  }

  function persistPendingPaymentState(nextPayment: PendingPayment) {
    updatePendingPayment(nextPayment);
    setPaymentPersistenceAvailable(
      persistPendingPayment(window.sessionStorage, nextPayment),
    );
  }

  function clearPendingPaymentDismissal(
    payment: PendingPayment,
  ): PendingPayment {
    const { dismissedAt: _dismissedAt, ...activePayment } = payment;
    return activePayment;
  }

  function beginInterruptedVerification() {
    verificationStartedAtRef.current = Date.now();
    setPaymentFlowPhase("interrupted");
    setVerificationMode("once");
    setVerificationActive(true);
    setVerificationNonce((current) => current + 1);
  }

  function closePaymentDialog() {
    closeTrackedPaymentPopup();
    if (
      checkoutPhase === "creating_invoice" ||
      checkoutPhase === "creating_payment"
    ) {
      paymentPreparationDismissedRef.current = true;
      setPaymentDialogOpen(false);
      return;
    }
    const currentPayment = pendingPaymentRef.current;
    if (currentPayment) {
      persistPendingPaymentState({
        ...currentPayment,
        dismissedAt: new Date().toISOString(),
      });
      beginInterruptedVerification();
    }
    setPaymentDialogOpen(false);
  }

  useEffect(() => {
    document.title = `${text.subscribe.title} | Kajikajy`;
  }, [text.subscribe.title]);

  useEffect(() => {
    const returnedPayment = parsePaymentReturnQuery(window.location.search);

    if (returnedPayment && window.opener && !window.opener.closed) {
      relayPaymentReturnToOpener(
        window.opener,
        returnedPayment.status,
        window.location.origin,
      );
      window.setTimeout(() => window.close(), 50);
      return;
    }

    if (
      returnedPayment?.returnApp === "account" &&
      returnedPayment.paymentIntentId
    ) {
      const accountUrl = new URL(getAccountHomeHref());
      accountUrl.searchParams.set("payment", returnedPayment.status);
      accountUrl.searchParams.set(
        "paymentIntentId",
        returnedPayment.paymentIntentId,
      );
      window.location.replace(accountUrl.toString());
      return;
    }

    if (returnedPayment) {
      window.history.replaceState(
        {},
        "",
        removePaymentReturnQuery(new URL(window.location.href)),
      );
    }

    const savedPayment = readPendingPayment(window.sessionStorage, "subscribe");
    if (
      savedPayment &&
      (!returnedPayment?.invoiceId ||
        returnedPayment.invoiceId === savedPayment.invoiceId)
    ) {
      const restoredPayment = returnedPayment
        ? clearPendingPaymentDismissal(savedPayment)
        : savedPayment;
      if (restoredPayment !== savedPayment) {
        persistPendingPaymentState(restoredPayment);
      } else {
        updatePendingPayment(restoredPayment);
        setPaymentPersistenceAvailable(true);
      }
      verificationStartedAtRef.current = Date.now();
      if (returnedPayment?.status === "success") {
        setPaymentFlowPhase("verifying");
        setVerificationMode("polling");
      } else {
        setPaymentFlowPhase("interrupted");
        setVerificationMode("once");
      }
      setVerificationActive(true);
      setPaymentDialogOpen(
        Boolean(returnedPayment) || !restoredPayment.dismissedAt,
      );
    }
  }, []);

  useEffect(() => {
    const handlePaymentMessage = (event: MessageEvent) => {
      const popup = paymentPopupRef.current;
      if (!popup || event.source !== popup) {
        return;
      }

      const isPapiMessage = isPapiPaymentStatusMessage(
        event,
        PAPI_PAYMENT_ORIGIN,
      );
      const isKajikajyReturn =
        isAllowedKajikajyPaymentOrigin(event.origin) &&
        isKajikajyPaymentReturnMessage(event);
      if (!isPapiMessage && !isKajikajyReturn) {
        return;
      }

      if (isKajikajyReturn) {
        closeTrackedPaymentPopup();
        const currentPayment = pendingPaymentRef.current;
        if (currentPayment?.dismissedAt) {
          persistPendingPaymentState(
            clearPendingPaymentDismissal(currentPayment),
          );
        }
        if (event.data.status === "failure") {
          beginInterruptedVerification();
          return;
        }
      }

      verificationStartedAtRef.current = Date.now();
      setPaymentFlowPhase("verifying");
      setVerificationMode("polling");
      setVerificationActive(true);
      setVerificationNonce((current) => current + 1);
    };

    window.addEventListener("message", handlePaymentMessage);
    return () => {
      window.removeEventListener("message", handlePaymentMessage);
      closeTrackedPaymentPopup();
    };
  }, []);

  useEffect(() => {
    if (!verificationActive || !pendingPayment?.invoiceId) {
      return undefined;
    }

    let disposed = false;
    let requestInFlight = false;
    let timer: number | null = null;
    let attempts = 0;
    let singleCheckCompleted = false;
    const verifyOnlyOnce = verificationMode === "once";
    const paymentExpiresAt = Date.parse(pendingPayment.expiresAt);

    const confirmationIsDelayed = () => {
      const startedAt = verificationStartedAtRef.current;
      return startedAt > 0 && Date.now() - startedAt >= 90_000;
    };

    const paymentHasExpired = () =>
      Number.isFinite(paymentExpiresAt) && Date.now() >= paymentExpiresAt;

    const schedule = (delay: number) => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      if (!disposed) {
        timer = window.setTimeout(() => void verify(), delay);
      }
    };

    const verify = async () => {
      if (disposed || requestInFlight || singleCheckCompleted) {
        return;
      }
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      if (document.hidden) {
        schedule(1000);
        return;
      }
      if (paymentHasExpired()) {
        setVerificationActive(false);
        setPaymentFlowPhase((current) =>
          current === "succeeded" || current === "failed" ? current : "delayed",
        );
        return;
      }

      requestInFlight = true;
      if (verifyOnlyOnce) {
        singleCheckCompleted = true;
      }
      let stopPolling = false;
      try {
        await ensureSubscriptionAuth();
        const result = await getPaymentStatus({
          invoiceId: pendingPayment.invoiceId!,
          paymentReference: pendingPayment.paymentReference,
        });
        if (disposed) {
          return;
        }

        const nextPhase = mapInvoiceStatusToPaymentPhase(result.status);
        if (nextPhase === "succeeded" || nextPhase === "failed") {
          stopPolling = true;
          closeTrackedPaymentPopup();
          clearPendingPayment(window.sessionStorage, "subscribe");
          updatePendingPayment(null);
          setPaymentPersistenceAvailable(true);
          setVerificationActive(false);
          setPaymentFlowPhase(nextPhase);
        } else if (confirmationIsDelayed()) {
          setPaymentFlowPhase("delayed");
        } else {
          setPaymentFlowPhase((current) =>
            current === "popup_open" || current === "interrupted"
              ? current
              : "verifying",
          );
        }
      } catch {
        if (!disposed && confirmationIsDelayed()) {
          setPaymentFlowPhase("delayed");
        }
      } finally {
        requestInFlight = false;
        if (!disposed && !stopPolling) {
          if (verifyOnlyOnce) {
            setVerificationActive(false);
            setPaymentFlowPhase((current) =>
              current === "succeeded" || current === "failed"
                ? current
                : "interrupted",
            );
          } else {
            attempts += 1;
            schedule(
              confirmationIsDelayed()
                ? 15_000
                : attempts === 1
                  ? 1500
                  : attempts === 2
                    ? 3000
                    : 5000,
            );
          }
        }
      }
    };

    const verifyWhenVisible = () => {
      if (!document.hidden) {
        void verify();
      }
    };
    window.addEventListener("focus", verifyWhenVisible);
    window.addEventListener("pageshow", verifyWhenVisible);
    document.addEventListener("visibilitychange", verifyWhenVisible);
    void verify();

    return () => {
      disposed = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
      window.removeEventListener("focus", verifyWhenVisible);
      window.removeEventListener("pageshow", verifyWhenVisible);
      document.removeEventListener("visibilitychange", verifyWhenVisible);
    };
  }, [pendingPayment, verificationActive, verificationMode, verificationNonce]);

  function openPaymentPopup() {
    const checkoutUrl =
      paymentSession?.checkoutUrl ?? pendingPayment?.checkoutUrl ?? null;
    const invoiceId = paymentSession?.invoiceId ?? pendingPayment?.invoiceId;
    if (!checkoutUrl || !invoiceId) {
      return;
    }

    closeTrackedPaymentPopup();
    const currentPayment = pendingPaymentRef.current;
    if (currentPayment?.dismissedAt) {
      persistPendingPaymentState(
        clearPendingPaymentDismissal(currentPayment),
      );
    }
    const popup = openCenteredPaymentPopup(
      checkoutUrl,
      `papi-payment-${invoiceId}`,
    );
    if (!popup) {
      setPaymentFlowPhase("popup_blocked");
      setVerificationActive(false);
      return;
    }

    paymentPopupRef.current = popup;
    verificationStartedAtRef.current = 0;
    setPaymentFlowPhase("popup_open");
    setVerificationMode("polling");
    setVerificationActive(true);
    setVerificationNonce((current) => current + 1);
    popup.focus();

    popupMonitorRef.current = window.setInterval(() => {
      if (!popup.closed) {
        return;
      }
      stopPopupMonitor();
      if (paymentPopupRef.current === popup) {
        paymentPopupRef.current = null;
      }
      if (!verificationStartedAtRef.current) {
        verificationStartedAtRef.current = Date.now();
      }
      setPaymentFlowPhase((current) =>
        current === "succeeded" || current === "failed"
          ? current
          : "interrupted",
      );
      setVerificationMode("once");
      setVerificationActive(true);
      setVerificationNonce((current) => current + 1);
    }, 500);
  }

  function continuePaymentInCurrentTab() {
    const checkoutUrl =
      paymentSession?.checkoutUrl ?? pendingPayment?.checkoutUrl ?? null;
    if (!checkoutUrl || !pendingPaymentRef.current) {
      setPaymentPersistenceAvailable(false);
      return;
    }
    const activePayment = clearPendingPaymentDismissal(
      pendingPaymentRef.current,
    );
    if (!persistPendingPayment(window.sessionStorage, activePayment)) {
      setPaymentPersistenceAvailable(false);
      return;
    }
    updatePendingPayment(activePayment);
    setPaymentPersistenceAvailable(true);
    closeTrackedPaymentPopup();
    setVerificationActive(false);
    navigateToPaymentCheckout(checkoutUrl);
  }

  function verifyPaymentNow() {
    if (!pendingPayment) {
      return;
    }
    verificationStartedAtRef.current = Date.now();
    setPaymentFlowPhase("verifying");
    setVerificationMode("once");
    setVerificationActive(true);
    setVerificationNonce((current) => current + 1);
  }

  useEffect(() => {
    if (window.location.hash !== "#facture") {
      return;
    }

    const requestedPlanId = new URLSearchParams(window.location.search).get(
      "plan",
    );
    if (!isKajikajyPlanId(requestedPlanId)) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      checkoutPanelRef.current?.scrollIntoView({ block: "start" });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, []);

  const selectedPlan = useMemo(
    () => KAJIKAJY_PLANS[selectedPlanId],
    [selectedPlanId],
  );
  const fullPhoneNumber = useMemo(
    () => buildInternationalPhoneNumber(phoneCountryCode, localPhoneNumber),
    [phoneCountryCode, localPhoneNumber],
  );
  const receiptEmail = email.trim();
  const receiptEmailIsValid =
    receiptEmail.length === 0 || isValidEmail(receiptEmail);
  const formIsValid =
    businessName.trim().length > 1 &&
    receiptEmailIsValid &&
    isValidLocalPhone(phoneCountryCode, localPhoneNumber) &&
    /^\d{4}$/.test(pin) &&
    pin === pinConfirmation;
  const isPreparingPayment =
    checkoutPhase === "creating_invoice" ||
    checkoutPhase === "creating_payment";
  const hasReusablePayment = Boolean(pendingPayment || paymentSession);

  useEffect(() => {
    const formSignature = [
      selectedPlanId,
      businessName,
      email,
      phoneCountryCode,
      localPhoneNumber,
      pin,
      pinConfirmation,
    ].join("\u0000");
    if (previousCheckoutFormRef.current === formSignature) {
      return;
    }
    if (verificationActive) {
      return;
    }
    previousCheckoutFormRef.current = formSignature;
    checkoutRequestIdRef.current += 1;
    closeTrackedPaymentPopup();
    clearPendingPayment(window.sessionStorage, "subscribe");
    updatePendingPayment(null);
    setPaymentPersistenceAvailable(true);
    setInvoice(null);
    setPaymentSession(null);
    setPaymentFlowPhase("ready");
    setPaymentDialogOpen(false);
    setFormError("");
    setCheckoutPhase("editing");
    paymentPreparationDismissedRef.current = false;
  }, [
    selectedPlanId,
    businessName,
    email,
    phoneCountryCode,
    localPhoneNumber,
    pin,
    pinConfirmation,
    verificationActive,
  ]);

  async function handleCreateInvoice(forceNew = false) {
    if (!forceNew) {
      const currentPayment = pendingPaymentRef.current;
      if (currentPayment) {
        if (currentPayment.dismissedAt) {
          persistPendingPaymentState(
            clearPendingPaymentDismissal(currentPayment),
          );
        }
        paymentPreparationDismissedRef.current = false;
        setCheckoutPhase("payment_ready");
        setPaymentFlowPhase((current) =>
          current === "succeeded" || current === "failed"
            ? current
            : "interrupted",
        );
        setPaymentDialogOpen(true);
        return;
      }
      if (paymentSession) {
        paymentPreparationDismissedRef.current = false;
        setPaymentDialogOpen(true);
        return;
      }
    }

    if (businessName.trim().length <= 1) {
      setFormError(text.subscribe.validationBusiness);
      return;
    }
    if (!receiptEmailIsValid) {
      setFormError(text.subscribe.validationEmail);
      return;
    }
    if (!isValidLocalPhone(phoneCountryCode, localPhoneNumber)) {
      setFormError(text.subscribe.validationPhone);
      return;
    }
    paymentPreparationDismissedRef.current = false;
    if (!/^\d{4}$/.test(pin)) {
      setFormError(text.subscribe.validationPin);
      return;
    }
    if (pin !== pinConfirmation) {
      setFormError(text.subscribe.validationPinMismatch);
      return;
    }
    const requestId = checkoutRequestIdRef.current + 1;
    checkoutRequestIdRef.current = requestId;
    const requestIsCurrent = () => checkoutRequestIdRef.current === requestId;

    if (forceNew) {
      closeTrackedPaymentPopup();
      clearPendingPayment(window.sessionStorage, "subscribe");
      updatePendingPayment(null);
      setPaymentPersistenceAvailable(true);
      setInvoice(null);
      setPaymentSession(null);
      setPaymentFlowPhase("ready");
      setVerificationActive(false);
    }

    setFormError("");
    setCheckoutPhase(
      invoice && !forceNew ? "creating_payment" : "creating_invoice",
    );
    setPaymentDialogOpen(true);
    try {
      let nextInvoice = forceNew ? null : invoice;
      if (!nextInvoice) {
        await ensureSubscriptionAuth();
        if (!requestIsCurrent()) {
          return;
        }
        nextInvoice = await createInvoice({
          businessName: businessName.trim(),
          ...(receiptEmail ? { customerEmail: receiptEmail } : {}),
          phoneNumber: fullPhoneNumber,
          planId: selectedPlanId,
          pin,
          pinConfirmation,
        });
        if (!requestIsCurrent()) {
          return;
        }
        setInvoice(nextInvoice);
      }

      if (!requestIsCurrent()) {
        return;
      }
      setCheckoutPhase("creating_payment");
      const nextPaymentSession = await createPaymentSession(
        nextInvoice.invoiceId,
        "papi",
      );
      if (!requestIsCurrent()) {
        return;
      }
      const completePaymentSession = {
        ...nextPaymentSession,
        amountMGA: resolveAmountMGA(
          nextPaymentSession.amountMGA,
          nextInvoice.amountMGA,
          selectedPlan.price.amountMGA,
        ),
        currency: nextPaymentSession.currency ?? nextInvoice.currency,
        expiresAt: nextPaymentSession.expiresAt ?? nextInvoice.expiresAt,
      };
      const dismissedDuringPreparation =
        paymentPreparationDismissedRef.current;
      paymentPreparationDismissedRef.current = false;
      const nextPendingPayment: PendingPayment = {
        version: 1,
        source: "subscribe",
        invoiceId: completePaymentSession.invoiceId,
        paymentReference: completePaymentSession.paymentReference,
        checkoutUrl: completePaymentSession.checkoutUrl ?? undefined,
        ...(dismissedDuringPreparation
          ? { dismissedAt: new Date().toISOString() }
          : {}),
        expiresAt: completePaymentSession.expiresAt,
        createdAt: new Date().toISOString(),
      };
      persistPendingPaymentState(nextPendingPayment);
      setPaymentSession(completePaymentSession);
      setPaymentFlowPhase(
        dismissedDuringPreparation ? "interrupted" : "ready",
      );
      setVerificationActive(false);
      verificationStartedAtRef.current = 0;
      setCheckoutPhase("payment_ready");
      setPaymentDialogOpen(!dismissedDuringPreparation);
    } catch (error) {
      if (!requestIsCurrent()) {
        return;
      }
      paymentPreparationDismissedRef.current = false;
      setCheckoutPhase("editing");
      const message = error instanceof Error ? error.message : "";
      setFormError(
        message.includes("Firebase web config")
          ? text.subscribe.firebaseMissing
          : text.subscribe.invoiceError,
      );
    }
  }

  const accountHref = getAccountHomeHref();
  const legalText = text.site;

  return (
    <main className="kj-shell page kj-page-atmosphere">
      <header className="kj-topbar">
        <a className="kj-brand" href={getSiteHref("/")} aria-label="Kajikajy">
          <img src="/kajikajy.png" alt="" />
          <span>Kajikajy</span>
        </a>
        <div className="kj-topbar-actions">
          <a className="kj-nav-link" href={accountHref}>
            {text.subscribe.accountLink}
          </a>
          <LanguageMenu
            label={text.common.language}
            locale={locale}
            onLocaleChange={setLocale}
          />
          <button
            className="kj-theme-toggle"
            type="button"
            aria-label={
              theme === "dark" ? text.common.lightMode : text.common.darkMode
            }
            onClick={toggleTheme}
          >
            {theme === "dark" ? (
              <Sun aria-hidden size={18} />
            ) : (
              <ThemeMoonIcon />
            )}
          </button>
        </div>
      </header>

      <section className="intro kj-page-atmosphere__opening">
        <div>
          <h1>{text.subscribe.title}</h1>
          <p className="lead">{text.subscribe.lead}</p>
        </div>
        <div className="trust-row" aria-label={text.subscribe.trustLabel}>
          <span>
            <ShieldCheck size={16} /> {text.subscribe.trustAccount}
          </span>
          <span>
            <CreditCard size={16} /> {text.subscribe.trustPayment}
          </span>
          <span>
            <Smartphone size={16} /> {text.subscribe.trustActivation}
          </span>
        </div>
      </section>

      <section className="content-grid">
        <div className="plan-list" aria-label={text.subscribe.plansLabel}>
          {plans.map((plan) => {
            const selected = plan.id === selectedPlanId;
            const planText = text.plans[plan.id];
            return (
              <button
                className={
                  selected
                    ? "kj-card kj-card--interactive kj-card--selected plan"
                    : "kj-card kj-card--interactive plan"
                }
                key={plan.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setSelectedPlanId(plan.id)}
              >
                <span className="plan-heading">
                  <span>
                    <strong>{plan.label}</strong>
                    <small>{planText.headline}</small>
                  </span>
                  {selected ? <CheckCircle2 aria-hidden size={22} /> : null}
                </span>
                <span className="price">{planText.priceMonthly}</span>
                <span className="features">
                  {planText.features.map((feature) => (
                    <span key={feature}>{feature}</span>
                  ))}
                </span>
              </button>
            );
          })}
        </div>

        <aside
          className="kj-card checkout-panel"
          id="facture"
          ref={checkoutPanelRef}
          aria-label={text.subscribe.invoicePanelLabel}
        >
          <div className="panel-title">
            <ReceiptText aria-hidden size={22} />
            <div>
              <strong>{text.subscribe.invoiceTitle}</strong>
              <span>
                {selectedPlan.label} -{" "}
                {text.plans[selectedPlan.id].priceMonthly}
              </span>
            </div>
          </div>

          <div className="form-section">
            <label className="kj-field">
              <span className="field-label">
                <Building2 aria-hidden size={15} />{" "}
                {text.subscribe.activityLabel}
              </span>
              <input
                autoComplete="organization"
                placeholder={text.subscribe.activityPlaceholder}
                value={businessName}
                onChange={(event) => setBusinessName(event.target.value)}
              />
            </label>

            <label className="kj-field">
              <span className="field-label">
                <Mail aria-hidden size={15} /> {text.subscribe.emailLabel}
              </span>
              <input
                autoComplete="email"
                inputMode="email"
                placeholder={text.subscribe.emailPlaceholder}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>

            <PhoneCountryField
              label={text.subscribe.phoneLabel}
              countryLabel={text.subscribe.phoneCountryLabel}
              searchPlaceholder={text.subscribe.phoneCountrySearch}
              noResults={text.subscribe.phoneCountryNoResults}
              placeholder={text.subscribe.phonePlaceholder}
              countryCode={phoneCountryCode}
              localNumber={localPhoneNumber}
              onCountryCodeChange={setPhoneCountryCode}
              onLocalNumberChange={setLocalPhoneNumber}
            />

            <div className="pin-fields">
              <label className="kj-field">
                <span className="field-label">
                  <ShieldCheck aria-hidden size={15} />{" "}
                  {text.subscribe.pinCreateLabel}
                </span>
                <input
                  autoComplete="new-password"
                  inputMode="numeric"
                  maxLength={4}
                  pattern="[0-9]*"
                  placeholder={text.subscribe.pinPlaceholder}
                  type="password"
                  value={pin}
                  onChange={(event) =>
                    setPin(event.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                />
              </label>

              <label className="kj-field">
                <span className="field-label">
                  <CheckCircle2 aria-hidden size={15} />{" "}
                  {text.subscribe.pinConfirmLabel}
                </span>
                <input
                  aria-invalid={
                    pinConfirmation.length === 4 && pin !== pinConfirmation
                  }
                  autoComplete="new-password"
                  inputMode="numeric"
                  maxLength={4}
                  pattern="[0-9]*"
                  placeholder={text.subscribe.pinPlaceholder}
                  type="password"
                  value={pinConfirmation}
                  onChange={(event) =>
                    setPinConfirmation(
                      event.target.value.replace(/\D/g, "").slice(0, 4),
                    )
                  }
                />
              </label>
            </div>
            {pinConfirmation.length === 4 && pin !== pinConfirmation ? (
              <p className="pin-inline-error" role="alert">
                {text.subscribe.validationPinMismatch}
              </p>
            ) : null}
          </div>

          <div className="summary-list">
            <div className="summary-row">
              <span>{text.subscribe.selectedOfferLabel}</span>
              <strong>{selectedPlan.label}</strong>
            </div>
            <div className="summary-row">
              <span>{text.subscribe.periodLabel}</span>
              <strong>{text.subscribe.periodValue}</strong>
            </div>
            <div className="summary-row">
              <span>{text.subscribe.amountLabel}</span>
              <strong>{text.plans[selectedPlan.id].priceMonthly}</strong>
            </div>
          </div>

          {invoice ? (
            <div className="payment-state">
              <strong>
                {text.subscribe.invoiceReadyTitle}
              </strong>
              <p>{text.subscribe.invoiceReadyText}</p>
              <div className="invoice-meta">
                <span>{text.subscribe.invoiceReferenceLabel}</span>
                <strong>{invoice.paymentReference}</strong>
              </div>
            </div>
          ) : null}

          {formError ? <p className="form-error">{formError}</p> : null}

          <button
            className="kj-button kj-button--primary kj-button--block cta"
            type="button"
            disabled={
              (!formIsValid && !hasReusablePayment) || isPreparingPayment
            }
            onClick={() => void handleCreateInvoice()}
          >
            {checkoutPhase === "creating_invoice"
              ? text.subscribe.creatingInvoice
              : checkoutPhase === "creating_payment"
                ? text.subscribe.creatingPayment
                : text.subscribe.continueButton}
            <ArrowRight aria-hidden size={18} />
          </button>
          <p className="panel-note">{text.subscribe.note}</p>
        </aside>
      </section>

      {paymentDialogOpen ? (
        <PaymentDialog
          accountHref={accountHref}
          checkoutUrl={
            paymentSession?.checkoutUrl ?? pendingPayment?.checkoutUrl ?? null
          }
          error={formError}
          flowPhase={paymentFlowPhase}
          onClose={closePaymentDialog}
          onContinueSameTab={continuePaymentInCurrentTab}
          onPay={openPaymentPopup}
          onRetry={() => void handleCreateInvoice(true)}
          onVerify={verifyPaymentNow}
          payment={paymentSession}
          phase={checkoutPhase}
          sameTabAvailable={paymentPersistenceAvailable}
          text={text.subscribe}
        />
      ) : null}

      <footer className="portal-footer">
        <nav aria-label={text.subscribe.legalLinksLabel}>
          <a href={getSiteHref(WEB_SITE_PATHS[locale].about)}>{legalText.about}</a>
          <a href={getSiteHref("/confidentialite")}>{legalText.legalPrivacy}</a>
          <a href={getSiteHref("/confidentialite-app")}>{legalText.legalAppPrivacy}</a>
          <a href={getSiteHref("/conditions")}>{legalText.legalTerms}</a>
          <a href={getSiteHref("/mentions-legales")}>{legalText.legalNotice}</a>
          <a href={getSiteHref("/suppression-de-compte")}>{legalText.legalDeletion}</a>
        </nav>
        <div className="portal-footer-copy">
          <FooterSocialLinks />
          <span>{legalText.copyright}</span>
        </div>
      </footer>
    </main>
  );
}
